// src/lib/render/explanation-structure.ts
//
// Lifts a PLAIN-PROSE question explanation into an easier-to-read structure,
// purely from the string — no regeneration, no schema. This feeds the
// free-text fallback of <ExplanationView/>, so questions that don't (yet) have
// a structured `richExplanation` still render something scannable instead of a
// dense wall of text.
//
// Three shapes, detected conservatively (when we're NOT confident we keep the
// original paragraph — the bar is "never make an explanation worse"):
//
//   1. distractors — the prose rules out specific wrong choices
//      ("…exactly what A states. Choice B overstates… Choice C is refuted…").
//      Rendered as a lead ("why the right answer is right") + a
//      "why the other choices are wrong" bulleted list, one bullet per choice.
//
//   2. steps — a multi-step derivation, almost always math
//      ("Let the integers be n, n+2, n+4. Then 3n+6 = 81. 3n = 75. n = 25.").
//      Rendered as a numbered list so each step is on its own line.
//
//   3. plain — anything short or single-thought. Rendered as today (a
//      paragraph), unchanged.
//
// Pure + framework-free so it can be unit-tested in isolation; the React
// rendering lives in ExplanationView.

export type ExplanationDistractor = { choice: string; why: string }

export type StructuredExplanation =
  | { kind: 'distractors'; lead: string; distractors: ExplanationDistractor[] }
  | { kind: 'steps'; steps: string[] }
  | { kind: 'plain'; text: string }

/** Matches "Choice B" / "Option C" (case-insensitive), capturing the letter. */
const ANCHOR_RE = /\b(?:choice|option)\s+([A-D])\b/gi

// Cue words that mark a choice as being RULED OUT (a distractor) rather than
// affirmed as correct. Deliberately broad — a false "negative" only means one
// extra bullet, never a dropped sentence. Checked against the text immediately
// following a "Choice X" anchor.
const NEGATIVE_CUE_RE =
  /\b(?:overstates?|understates?|too\s+(?:narrow|broad|strong|weak|extreme|specific|general)|is\s+(?:wrong|incorrect|unsupported|not\b|never\b)|incorrect|unsupported|no\s+support|not\s+supported|refut\w*|contradicts?|contradict\w*|fails?|doesn'?t|does\s+not|isn'?t|aren'?t|cannot|can'?t|misreads?|misrepresents?|reverses?|confus\w*|ignores?|irrelevant|opposite|distorts?|exaggerat\w*|out\s+of\s+scope|never\s+stated|not\s+mentioned|goes\s+beyond|only\b)\b/i

/**
 * Decide whether the snippet that follows a "Choice X" anchor describes a wrong
 * choice (a distractor) vs. affirms the correct one ("Choice A captures…").
 * We look only at the first stretch after the anchor.
 */
function looksLikeDistractor(why: string): boolean {
  return NEGATIVE_CUE_RE.test(why.slice(0, 80))
}

/** Strip leading joiners/punctuation left over after slicing off the anchor, so
 *  a bullet reads "B. overstates the evidence…" / "C. is refuted by…". */
function cleanWhy(s: string): string {
  return s.replace(/^[\s:.,;—–-]+/, '').trim()
}

type Anchor = { letter: string; start: number; afterLetter: number }

function findAnchors(text: string): Anchor[] {
  const anchors: Anchor[] = []
  ANCHOR_RE.lastIndex = 0
  let m: RegExpExecArray | null
  while ((m = ANCHOR_RE.exec(text)) !== null) {
    anchors.push({
      letter: m[1].toUpperCase(),
      start: m.index,
      afterLetter: m.index + m[0].length,
    })
  }
  return anchors
}

/**
 * Try to read the text as a "lead + per-distractor" explanation. Returns null
 * unless at least TWO distinct choices are confidently ruled out — a lone
 * "Choice A captures the main idea" is an affirmation, not a breakdown, and
 * stays a paragraph.
 */
function tryDistractors(
  text: string,
  correctChoice?: string | null,
): Extract<StructuredExplanation, { kind: 'distractors' }> | null {
  const anchors = findAnchors(text)
  if (anchors.length < 2) return null

  const correct = correctChoice?.trim().toUpperCase() || null

  // Build a candidate "why" for each anchor: the text from after the letter up
  // to the next anchor (or end of string).
  const segments = anchors.map((a, i) => {
    const end = i + 1 < anchors.length ? anchors[i + 1].start : text.length
    return { ...a, why: cleanWhy(text.slice(a.afterLetter, end)) }
  })

  // A choice is a distractor if it's not the known-correct one AND its blurb
  // reads like a rule-out.
  const distractorAnchors = segments.filter(
    (s) => s.letter !== correct && looksLikeDistractor(s.why),
  )
  if (distractorAnchors.length < 2) return null

  // The lead is everything before the FIRST ruled-out choice — this keeps any
  // "…which is exactly what A states" correct-answer reasoning in the lead.
  const firstDistractorStart = distractorAnchors[0].start
  const lead = text.slice(0, firstDistractorStart).trim()
  if (!lead) return null

  // De-dupe by letter (first mention wins), keep choice order A→B→C→D.
  const seen = new Set<string>()
  const distractors: ExplanationDistractor[] = []
  for (const s of distractorAnchors) {
    if (seen.has(s.letter) || !s.why) continue
    seen.add(s.letter)
    distractors.push({ choice: s.letter, why: s.why })
  }
  distractors.sort((a, b) => a.choice.localeCompare(b.choice))
  if (distractors.length < 2) return null

  return { kind: 'distractors', lead, distractors }
}

// Split on sentence/clause boundaries WITHOUT breaking decimals (3.5), ellipses,
// or "n + 2" lists: a boundary is a period/semicolon followed by whitespace.
function splitSegments(text: string): string[] {
  return text
    .split(/(?<=[.;])\s+/)
    .map((s) => s.trim())
    .filter(Boolean)
}

/**
 * Try to read the text as a multi-step derivation. Conservative: only fires for
 * mathy content (an "=" somewhere) with at least three segments, so ordinary
 * two-sentence prose is left as a paragraph.
 */
function trySteps(text: string): Extract<StructuredExplanation, { kind: 'steps' }> | null {
  if (!text.includes('=')) return null
  const segments = splitSegments(text)
  if (segments.length < 3) return null
  return { kind: 'steps', steps: segments }
}

/**
 * Structure a plain-text explanation for readable rendering. Always returns a
 * value; `plain` means "render as a paragraph" (the original behavior).
 */
export function structureExplanation(
  text: string | null | undefined,
  opts?: { correctChoice?: string | null },
): StructuredExplanation {
  const trimmed = (text ?? '').trim()
  if (!trimmed) return { kind: 'plain', text: '' }

  const distractors = tryDistractors(trimmed, opts?.correctChoice)
  if (distractors) return distractors

  const steps = trySteps(trimmed)
  if (steps) return steps

  return { kind: 'plain', text: trimmed }
}
